/**
 * Unified Pricing Service
 *
 * Single source of truth for all cost calculations across the app:
 * - Calculator Tool, Booking Wizard, Invoicing Admin
 *
 * Handles: templates, transport (tariffs), LMD (rates + settings), additional
 * costs (aanvullende kosten), export clearance, pickup fee.
 */

import { supabase } from "../lib/supabase";
import type { Tariff } from "../hooks/useTariffs";
import { EUROPALLET } from "../lib/pricingDefaults";
import {
  calculateEuropalletNonExchangeFee,
  europalletNonExchangeDescription,
} from "../utils/europalletFee";
import {
  calculateLmdCost,
  getDieselSurchargePercentage,
  getPalletThreshold,
  CBM_PER_PALLET,
  LmdRateNotFoundError,
} from "./lmdRatesService";

// ============================================================================
// COMPONENT TYPES
// ============================================================================

interface PricingTemplateComponent {
  id: number;
  pricing_component_id: number;
  value: number | null;
  is_active: boolean;
  pricing_components: {
    id: number;
    name: string;
    description: string;
    calculation_type: string;
    component_type: string;
    behavior_type: string;
    applies_to_delivery_type: string;
    applies_to_shipment_type: string;
  };
}

interface MergedComponent extends PricingTemplateComponent {
  component_id: number; // Alias for pricing_component_id
  source: "base" | "child";
  base_template_name?: string;
  child_template_name?: string;
}

// ============================================================================
// ADDITIONAL COSTS HELPERS
// ============================================================================

interface AdditionalCost {
  id: number;
  name: string;
  description: string | null;
  cost_eur: number | null;
  calculation_type: "fixed" | "user_input" | "percentage";
  default_value: number | null;
  max_value: number | null;
  applies_to_countries: string[] | null;
  applies_to_incoterms: Array<number | string> | null;
  is_active: boolean;
}

/** Additional cost id for "Fiscale vertegenwoordiging - België". When selected, Inklaringskosten is excluded; €6.50 per extra HS code is added to this cost. */
const FISCALE_VERTEGENWOORDIGING_ADDITIONAL_COST_ID = 3;
/** Pricing component id for "Inklaringskosten". Excluded when Fiscale vertegenwoordiging is selected. */
const INKLARINGSKOSTEN_COMPONENT_ID = 25;
/** When Fiscale vertegenwoordiging is selected: €6.50 per extra HS code (beyond the 1st) is added to the base cost. */
const FISCALE_VERTEGENWOORDIGING_EXTRA_PER_HS_EUR = 6.5;

const COUNTRY_CODE_MAP: Record<string, string> = {
  NETHERLANDS: "NL",
  "THE NETHERLANDS": "NL",
  HOLLAND: "NL",
  BELGIUM: "BE",
  "KINGDOM OF BELGIUM": "BE",
  GERMANY: "DE",
  FRANCE: "FR",
  SPAIN: "ES",
  ITALY: "IT",
  LUXEMBOURG: "LU",
  DENMARK: "DK",
  SWEDEN: "SE",
  NORWAY: "NO",
  FINLAND: "FI",
  POLAND: "PL",
  "UNITED KINGDOM": "GB",
  "GREAT BRITAIN": "GB",
  ENGLAND: "GB",
  UK: "GB",
};

const normalizeCountryCode = (value?: string | null): string | null => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parenthesesMatch = trimmed.match(/\(([A-Z]{2})\)$/i);
  if (parenthesesMatch) {
    return parenthesesMatch[1].toUpperCase();
  }

  if (/^[A-Z]{2}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  const upper = trimmed.toUpperCase();
  if (COUNTRY_CODE_MAP[upper]) {
    return COUNTRY_CODE_MAP[upper];
  }

  const trailingCodeMatch = upper.match(/([A-Z]{2})$/);
  if (trailingCodeMatch) {
    return trailingCodeMatch[1].toUpperCase();
  }

  return null;
};

const parseNumericValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const getApplicableCountryCodes = (options: {
  deliveryCountryCode?: string | null;
  destinationCountryCode?: string | null;
} = {}): string[] => {
  const codes = new Set<string>();

  const deliveryCode = normalizeCountryCode(options.deliveryCountryCode);
  if (deliveryCode) {
    codes.add(deliveryCode);
  }

  const destinationCode = normalizeCountryCode(options.destinationCountryCode);
  if (destinationCode) {
    codes.add(destinationCode);
  }

  return Array.from(codes);
};

export const filterAdditionalCosts = (
  costs: AdditionalCost[],
  options: {
    deliveryCountryCode?: string | null;
    destinationCountryCode?: string | null;
    incotermId?: number | null;
  } = {}
): AdditionalCost[] => {
  const applicableCountryCodes = getApplicableCountryCodes(options);

  return costs.filter((cost) => {
    const matchesCountry =
      !cost.applies_to_countries ||
      cost.applies_to_countries.length === 0 ||
      applicableCountryCodes.length === 0 ||
      cost.applies_to_countries
        .map((country) => normalizeCountryCode(country))
        .filter(Boolean)
        .some((code) =>
          applicableCountryCodes.includes(code as string)
        );

    if (!matchesCountry) {
      return false;
    }

    if (
      cost.applies_to_incoterms &&
      cost.applies_to_incoterms.length > 0
    ) {
      if (!options.incotermId) {
        return false;
      }

      const normalizedIncoterms = cost.applies_to_incoterms.map((id) =>
        typeof id === "number" ? id : Number(id)
      );
      return normalizedIncoterms.includes(options.incotermId);
    }

    return true;
  });
};

export const calculateAdditionalCostAmount = (
  cost: AdditionalCost,
  context: { totalValue?: number } = {}
): number => {
  const baseValue = parseNumericValue(cost.cost_eur);
  const defaultValue = parseNumericValue(cost.default_value);
  const resolvedValue = baseValue ?? defaultValue ?? 0;

  switch (cost.calculation_type) {
    case "percentage": {
      if (!context.totalValue || context.totalValue <= 0) {
        return 0;
      }
      return context.totalValue * (resolvedValue / 100);
    }
    case "user_input":
      return defaultValue ?? baseValue ?? 0;
    default:
      return resolvedValue;
  }
};

async function fetchFixedAdditionalCostsForCountry(
  country?: string,
  incotermId?: number | null
): Promise<AdditionalCost[]> {
  try {
    const { data, error } = await supabase
      .from("additional_costs")
      .select("*")
      .eq("is_active", true);

    if (error || !data) {
      return [];
    }

    return filterAdditionalCosts(data as AdditionalCost[], {
      deliveryCountryCode: country,
      incotermId,
    }).filter((cost) => cost.calculation_type === "fixed");
  } catch (error) {
    console.error("Error fetching additional costs:", error);
    return [];
  }
}

/**
 * Fetch variable costs that can be set by users (for booking forms)
 */
export async function fetchVariableAdditionalCosts(): Promise<AdditionalCost[]> {
  try {
    const { data, error } = await supabase
      .from("additional_costs")
      .select("*")
      .eq("is_active", true)
      .in("calculation_type", ["user_input", "percentage"])
      .order("name");

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching variable additional costs:", error);
    return [];
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface PricingContext {
  transportTypeId: number | null;
  bookingTypeId?: number | null; // 1 = LCL, 2 = FCL (for seafreight)
  incotermId: number | null;
  originPortId: number | null;
  destinationPortId: number | null;
  containerSize?: number | null; // For FCL bookings
  matchDate?: string; // Date to match templates against (defaults to today)
}

export interface BookingData {
  totalCartons: number;
  totalPallets: number;
  uniqueProducts: number;
  totalWeight: number; // kg
  totalVolume: number; // CBM
  uniqueHsCodesCount: number;
  totalValue?: number; // For percentage calculations
  containerSize?: number;
}

export interface TemplateMatch {
  template: Record<string, unknown>;
  score: number;
  matchingCriteria: string[];
  missingCriteria: string[];
}

export interface MergedComponent {
  component_id: number;
  template_id: number;
  template_name: string;
  template_score: number;
  value: number | null;
  currency: string;
  included_quantity: number | null;
  extra_price: number | null;
  is_active: boolean;
  pricing_components: {
    id: number;
    name: string;
    description: string;
    calculation_type: string;
    component_type: string;
    behavior_type: string;
    applies_to_delivery_type: string;
    applies_to_shipment_type: string;
    applies_to_booking_type?: "fcl" | "lcl" | "both" | null;
  };
  source: "base" | "child" | "override";
  base_template_name?: string;
}

export interface CalculatedComponent {
  componentId: number;
  name: string;
  description: string | null;
  componentType: string;
  calculationType: string;
  value: number;
  currency: string;
  quantity: number;
  totalPrice: number;
  unitPrice: number;
  calculationDetails?: string;
  // Internal field for reference (removed before returning)
  _mergedComponent?: MergedComponent;
}

export interface PricingResult {
  components: CalculatedComponent[];
  totalCost: number;
  currency: string;
  matchedTemplates: Record<string, unknown>[];
  matchingCriteria: string[];
  error: string | null;
  /**
   * Non-fatal issues (e.g. LMD rate lookup failed while rest of quote succeeded).
   * Translate in the UI with `t(warning.key, warning.params)` so language switches apply.
   */
  warnings: PricingWarning[];
}

/** Structured warning so the calculator can translate on render (language switch). */
export interface PricingWarning {
  key: string;
  params?: Record<string, string | number>;
}

// ============================================================================
// TEMPLATE MATCHING
// ============================================================================

/**
 * Find all matching pricing templates using scoring system
 */
export async function findMatchingTemplates(
  context: PricingContext,
  allTemplates: Record<string, unknown>[]
): Promise<TemplateMatch[]> {
  const {
    transportTypeId,
    bookingTypeId,
    incotermId,
    originPortId,
    destinationPortId,
    containerSize,
    matchDate = new Date().toISOString().split("T")[0],
  } = context;

  const templateMatches: TemplateMatch[] = [];

  allTemplates.forEach((template) => {
    let score = 0;
    const matchingCriteria: string[] = [];
    const missingCriteria: string[] = [];

    // Check transport type
    if (template.transport_type === transportTypeId) {
      score += 10;
      matchingCriteria.push("Transport Type");
    } else if (template.transport_type !== null) {
      missingCriteria.push("Transport Type");
    }

    // Check booking type
    if (template.booking_type === bookingTypeId) {
      score += 10;
      matchingCriteria.push("Booking Type");
    } else if (
      template.booking_type !== null &&
      bookingTypeId !== null
    ) {
      missingCriteria.push("Booking Type");
    }

    // Check incoterm
    if (template.incoterm === incotermId) {
      score += 10;
      matchingCriteria.push("Incoterm");
    } else if (template.incoterm !== null) {
      missingCriteria.push("Incoterm");
    }

    // Check origin port
    if (template.origin_port === originPortId) {
      score += 10;
      matchingCriteria.push("Origin Port");
    } else if (template.origin_port !== null) {
      missingCriteria.push("Origin Port");
    }

    // Check destination port
    if (template.destination_port === destinationPortId) {
      score += 10;
      matchingCriteria.push("Destination Port");
    } else if (template.destination_port !== null) {
      missingCriteria.push("Destination Port");
    }

    // Check container size (only for FCL)
    if (bookingTypeId === 2 && template.container_size === containerSize) {
      score += 10;
      matchingCriteria.push("Container Size");
    } else if (template.container_size !== null && containerSize !== null) {
      missingCriteria.push("Container Size");
    }

    // Check date validity
    const isDateValid =
      (!template.valid_from ||
        template.valid_from <= matchDate) &&
      (!template.valid_to || template.valid_to >= matchDate);

    if (!isDateValid) {
      return; // Skip templates outside validity period
    }

    // Only include templates with at least one match AND no missing criteria for specified fields
    if (score > 0) {
      const hasMissingRequiredCriteria = missingCriteria.some((criteria) => {
        switch (criteria) {
          case "Transport Type":
            return transportTypeId !== null;
          case "Booking Type":
            return bookingTypeId !== null;
          case "Incoterm":
            return incotermId !== null;
          case "Origin Port":
            return originPortId !== null;
          case "Destination Port":
            return destinationPortId !== null;
          case "Container Size":
            return containerSize !== null;
          default:
            return false;
        }
      });

      if (!hasMissingRequiredCriteria) {
        templateMatches.push({
          template,
          score,
          matchingCriteria,
          missingCriteria,
        });
      }
    }
  });

  // Sort by score (highest first)
  templateMatches.sort((a, b) => b.score - a.score);

  return templateMatches;
}

// ============================================================================
// COMPONENT MERGING (with inheritance)
// ============================================================================

/**
 * Get merged template components handling inheritance
 */
export function getMergedTemplateComponents(
  template: Record<string, unknown>,
  allTemplates: Record<string, unknown>[]
): MergedComponent[] {
  const mergedComponents: MergedComponent[] = [];

  // Get base template components if this template inherits
  if (template.inherit_from_template_id) {
    const baseTemplate = allTemplates.find(
      (t) => t.id === template.inherit_from_template_id
    );
    if (baseTemplate && baseTemplate.pricing_template_components) {
      baseTemplate.pricing_template_components.forEach((baseTc: PricingTemplateComponent) => {
        if (baseTc.is_active && baseTc.value !== null) {
          mergedComponents.push({
            ...baseTc,
            source: "base",
            base_template_name: baseTemplate.name,
          });
        }
      });
    }
  }

  // Add/override with child template components
  if (template.pricing_template_components) {
    template.pricing_template_components.forEach((childTc: PricingTemplateComponent) => {
      if (childTc.is_active && childTc.value !== null) {
        const existingIndex = mergedComponents.findIndex(
          (mc) => mc.component_id === childTc.component_id
        );

        if (existingIndex >= 0) {
          // Override base component with child component
          mergedComponents[existingIndex] = {
            ...childTc,
            source: "override",
            base_template_name:
              mergedComponents[existingIndex].base_template_name,
          };
        } else {
          // Add new component from child template
          mergedComponents.push({
            ...childTc,
            source: "child",
          });
        }
      }
    });
  }

  return mergedComponents;
}

/**
 * Merge components from multiple templates (prioritizing higher-scoring templates)
 */
export function mergeComponentsFromTemplates(
  templateMatches: TemplateMatch[],
  allTemplates: Record<string, unknown>[]
): MergedComponent[] {
  const componentMap = new Map<number, MergedComponent>();

  for (const match of templateMatches) {
    const templateComponents = getMergedTemplateComponents(
      match.template,
      allTemplates
    );

    templateComponents.forEach((tc: MergedComponent) => {
      const componentId = tc.component_id;
      const existingComponent = componentMap.get(componentId);

      if (!existingComponent) {
        // New component - add it with template info
        componentMap.set(componentId, {
          ...tc,
          template_name: match.template.name,
          template_score: match.score,
        });
      } else {
        // Component exists - keep the one from the higher scoring template
        if (match.score > existingComponent.template_score) {
          componentMap.set(componentId, {
            ...tc,
            template_name: match.template.name,
            template_score: match.score,
            base_template_name: existingComponent.base_template_name,
          });
        }
      }
    });
  }

  return Array.from(componentMap.values());
}

function isTemplateValidOnDate(
  template: Record<string, unknown>,
  matchDate: string
): boolean {
  return (
    (!template.valid_from || (template.valid_from as string) <= matchDate) &&
    (!template.valid_to || (template.valid_to as string) >= matchDate)
  );
}

/** FCL seafreight terminal / opslag surcharges (see pricing_components ids 34–38). */
function isFclTerminalSurchargeComponentName(name: string | undefined): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return (
    n.includes("terminal handling") ||
    n.includes("euromax") ||
    n.includes("spreidingstoeslag") ||
    n.includes("lossingskosten")
  );
}

/**
 * Port-specific templates may omit FCL terminal lines; pull them from any valid
 * sea+FCL template that matches incoterm (preferred) or generic FCL template.
 */
function supplementFclTerminalComponents(
  context: PricingContext,
  allTemplates: Record<string, unknown>[],
  mergedComponents: MergedComponent[],
  matchDate: string
): MergedComponent[] {
  if (context.transportTypeId !== 2 || context.bookingTypeId !== 2) {
    return mergedComponents;
  }

  const existingIds = new Set(mergedComponents.map((m) => m.component_id));
  const supplemented = [...mergedComponents];

  const addFromTemplate = (template: Record<string, unknown>, preferIncoterm: boolean) => {
    if (template.incoterm != null && template.incoterm !== context.incotermId) return;
    if (preferIncoterm && template.incoterm == null) return;

    const components = getMergedTemplateComponents(template, allTemplates);
    for (const tc of components) {
      if (!tc.pricing_components || !tc.is_active || tc.value === null) continue;
      if (!isFclTerminalSurchargeComponentName(tc.pricing_components.name)) continue;
      if (existingIds.has(tc.component_id)) continue;

      supplemented.push({
        ...tc,
        template_name: (template.name as string) || "FCL supplement",
        template_score: 0,
        source: "child",
      });
      existingIds.add(tc.component_id);
    }
  };

  const fclTemplates = allTemplates.filter((template) => {
    if (!isTemplateValidOnDate(template, matchDate)) return false;
    if (template.transport_type !== 2) return false;
    if (template.booking_type != null && template.booking_type !== 2) return false;
    if (
      template.container_size != null &&
      context.containerSize != null &&
      template.container_size !== context.containerSize
    ) {
      return false;
    }
    return true;
  });

  for (const template of fclTemplates.filter((t) => t.incoterm === context.incotermId)) {
    addFromTemplate(template, true);
  }
  for (const template of fclTemplates.filter((t) => t.incoterm == null)) {
    addFromTemplate(template, false);
  }

  return supplemented;
}

// ============================================================================
// COMPONENT VALUE CALCULATION
// ============================================================================

/**
 * Calculate component value based on calculation type
 */
export function calculateComponentValue(
  component: MergedComponent,
  bookingData: BookingData,
  baseValue: number,
  options?: {
    isFCL?: boolean;
    isSeafreight?: boolean;
    formatEuro?: (value: number) => string;
    t?: (key: string, defaultValue?: string) => string;
  }
): { quantity: number; totalPrice: number; calculationDetails?: string } {
  const calculationType = component.pricing_components?.calculation_type;
  const { isFCL = false, isSeafreight = false, formatEuro, t } = options || {};
  let quantity = 1;
  let totalPrice = baseValue;
  let calculationDetails: string | undefined;

  switch (calculationType) {
    case "per_carton":
      quantity = bookingData.totalCartons;
      totalPrice = baseValue * quantity;
      if (formatEuro && t) {
        calculationDetails = `${quantity} ${t("tools.calculator.cartons", "cartons")} × ${formatEuro(baseValue)}`;
      }
      break;

    case "per_pallet":
      quantity = bookingData.totalPallets;
      totalPrice = baseValue * quantity;
      if (formatEuro && t) {
        calculationDetails = `${quantity} ${t("tools.calculator.pallets", "pallets")} × ${formatEuro(baseValue)}`;
      }
      break;

    case "per_unique_product": {
      // Tiered flat fee: base covers included_quantity HS codes; extras add extra_price.
      // Bill as a single line (qty 1); do NOT multiply base × HS count.
      const hsCount = bookingData.uniqueHsCodesCount;
      quantity = 1;
      if (component.included_quantity && hsCount > component.included_quantity) {
        const extraProducts = hsCount - component.included_quantity;
        const extraCost = (component.extra_price || 0) * extraProducts;
        totalPrice = baseValue + extraCost;
        if (formatEuro && t) {
          calculationDetails = `${formatEuro(baseValue)} (${component.included_quantity} ${t("tools.calculator.included", "included")}) + ${extraProducts} × ${formatEuro(component.extra_price || 0)} = ${formatEuro(totalPrice)}`;
        }
      } else {
        totalPrice = baseValue;
        if (formatEuro) {
          calculationDetails = `1 × ${formatEuro(baseValue)}`;
        }
      }
      break;
    }

    case "per_product": {
      // Same tiered model as per_unique_product, using unique product count.
      const productCount = bookingData.uniqueProducts;
      quantity = 1;
      if (component.included_quantity && productCount > component.included_quantity) {
        const extraProducts = productCount - component.included_quantity;
        const extraCost = (component.extra_price || 0) * extraProducts;
        totalPrice = baseValue + extraCost;
        if (formatEuro && t) {
          calculationDetails = `${formatEuro(baseValue)} (${component.included_quantity} ${t("tools.calculator.included", "included")}) + ${extraProducts} × ${formatEuro(component.extra_price || 0)} = ${formatEuro(totalPrice)}`;
        }
      } else {
        totalPrice = baseValue;
        if (formatEuro) {
          calculationDetails = `1 × ${formatEuro(baseValue)}`;
        }
      }
      break;
    }

    case "per_kg":
      quantity = bookingData.totalWeight;
      totalPrice = baseValue * quantity;
      if (formatEuro) {
        calculationDetails = `${quantity.toFixed(2)} kg × ${formatEuro(baseValue)}`;
      }
      break;

    case "per_cbm":
      // For FCL Seafreight, ignore volume-based calculations
      if (isFCL && isSeafreight) {
        quantity = 0;
        totalPrice = 0;
      } else {
        quantity = bookingData.totalVolume;
        totalPrice = baseValue * quantity;
        if (formatEuro && quantity > 0) {
          calculationDetails = `${quantity} CBM × ${formatEuro(baseValue)}`;
        }
      }
      break;

    case "per_container":
      quantity = 1;
      totalPrice = baseValue;
      if (formatEuro && t) {
        calculationDetails = `1 ${t("tools.calculator.container", "container")} × ${formatEuro(baseValue)}`;
      }
      break;

    case "per_shipment":
    case "fixed": {
      // Admin outgoing fees are priced per pallet/carton even when stored as fixed.
      const componentName = component.pricing_components?.name || "";
      if (
        /administration fee outgoing pallet/i.test(componentName) &&
        bookingData.totalPallets > 0
      ) {
        quantity = bookingData.totalPallets;
        totalPrice = baseValue * quantity;
        if (formatEuro) {
          calculationDetails = `${quantity} × ${formatEuro(baseValue)}`;
        }
        break;
      }
      if (
        /administration fee outgoing carton/i.test(componentName) &&
        bookingData.totalCartons > 0
      ) {
        quantity = bookingData.totalCartons;
        totalPrice = baseValue * quantity;
        if (formatEuro) {
          calculationDetails = `${quantity} × ${formatEuro(baseValue)}`;
        }
        break;
      }
      quantity = 1;
      totalPrice = baseValue;
      if (formatEuro) {
        calculationDetails = `1 × ${formatEuro(baseValue)}`;
      }
      break;
    }

    case "percentage":
      // Percentage calculations require base_value (totalValue)
      if (bookingData.totalValue !== undefined && bookingData.totalValue > 0) {
        quantity = 1;
        totalPrice = bookingData.totalValue * (baseValue / 100);
        if (formatEuro) {
          calculationDetails = `${formatEuro(bookingData.totalValue)} × ${baseValue}%`;
        }
      } else {
        quantity = 0;
        totalPrice = 0;
        calculationDetails = `${baseValue}% (base value not available)`;
      }
      break;

    default:
      quantity = 1;
      totalPrice = baseValue;
      if (formatEuro) {
        calculationDetails = `1 × ${formatEuro(baseValue)}`;
      }
  }

  return { quantity, totalPrice, calculationDetails };
}

// ============================================================================
// MAIN PRICING CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate pricing from templates (unified logic for all three places)
 */
/** FOB incoterm ID — export clearance does not apply */
const INCOTERM_FOB_ID = 3;

export interface UnifiedPricingOptions {
  selectedTariff?: Tariff | null;
  isStorageDelivery?: boolean;
  companyId?: number;
  companyOverrides?: Record<string, unknown>[];
  formatEuro?: (value: number) => string;
  t?: (key: string, defaultValue?: string) => string;
  filterZeroComponents?: boolean;
  /** Delivery vs storage; when "delivery", LMD may be applied */
  deliveryType?: "delivery" | "storage";
  /**
   * @deprecated No longer passed from UI. Effective unloading is derived:
   * - FCL + Delivery = at_location (direct to customer, geen lossingskosten)
   * - FCL + Storage = at_efreight (goods at E-Freight, wel lossingskosten)
   * - LCL/Airfreight = at_efreight (always)
   */
  unloadingLocation?: "at_location" | "at_efreight" | null;
  /** LMD delivery country code (e.g. NL) when deliveryType === "delivery" */
  lmdCountryCode?: string | null;
  /** Destination country code for additional-cost filtering */
  destinationCountryCode?: string | null;
  /** Pallet threshold (cartons >= this → pallet mode). From lmd_settings.pallet_threshold. */
  palletThreshold?: number;
  /** Additional cost IDs to include (e.g. user-selected in calculator) */
  selectedAdditionalCostIds?: number[];
  /** Override amount per additional cost ID */
  additionalCostOverrides?: Record<number, number>;
  /** Add export clearance component when true and not FOB */
  includeExportClearance?: boolean;
  /** Export clearance amount when includeExportClearance; default 45 */
  exportClearanceAmount?: number;
  /** Pickup fee amount to add when > 0 */
  pickupFee?: number;
  /** Add ISCM commodity inspection fee when true; default €75 */
  includeCommodityInspection?: boolean;
  /** Commodity inspection amount when includeCommodityInspection; default 75 */
  commodityInspectionAmount?: number;
  /** Post export license fee amount to add when > 0 */
  postExportLicenseFee?: number;
  /** Selected container size label for FCL (e.g. "20ft") – used for Transport Cost label in cost breakdown */
  containerSizeName?: string | null;
  /**
   * When false and totalPallets > 0, add €15/pallet for non-exchanged europallets.
   * Default true (geruild = no fee). Only relevant in pallet shipment mode.
   */
  europalletsExchanged?: boolean;
}

export async function calculatePricingFromTemplates(
  context: PricingContext,
  bookingData: BookingData,
  options?: UnifiedPricingOptions
): Promise<PricingResult> {
  const {
    selectedTariff,
    isStorageDelivery = false,
    companyId,
    companyOverrides = [],
    formatEuro,
    t,
    filterZeroComponents = true,
    deliveryType = "storage",
    unloadingLocation,
    lmdCountryCode,
    destinationCountryCode,
    palletThreshold: palletThresholdOpt,
    selectedAdditionalCostIds = [],
    additionalCostOverrides = {},
    includeExportClearance = false,
    exportClearanceAmount = 45,
    pickupFee = 0,
    includeCommodityInspection = false,
    commodityInspectionAmount = 75,
    postExportLicenseFee = 0,
    containerSizeName: containerSizeNameOpt = null,
    europalletsExchanged = true,
  } = options || {};

  try {
    // Fetch all active pricing templates with components
    const { data: allTemplates, error: templatesError } = await supabase
      .from("pricing_templates")
      .select(
        `
        *,
        pricing_template_components (
          *,
          pricing_components (*)
        )
      `
      )
      .eq("is_active", true);

    if (templatesError || !allTemplates) {
      return {
        components: [],
        totalCost: 0,
        currency: "EUR",
        matchedTemplates: [],
        matchingCriteria: [],
        error: "Failed to fetch pricing templates",
        warnings: [],
      };
    }

    // Find matching templates
    const templateMatches = await findMatchingTemplates(context, allTemplates);

    if (templateMatches.length === 0) {
      return {
        components: [],
        totalCost: 0,
        currency: "EUR",
        matchedTemplates: [],
        matchingCriteria: [],
        error: "No matching pricing templates found",
        warnings: [],
      };
    }

    const matchDate =
      context.matchDate || new Date().toISOString().split("T")[0];

    // Merge components from all matching templates
    let mergedComponents = mergeComponentsFromTemplates(
      templateMatches,
      allTemplates
    );

    mergedComponents = supplementFclTerminalComponents(
      context,
      allTemplates,
      mergedComponents,
      matchDate
    );

    if (mergedComponents.length === 0) {
      return {
        components: [],
        totalCost: 0,
        currency: templateMatches[0].template.currency || "EUR",
        matchedTemplates: templateMatches.map((m) => m.template),
        matchingCriteria: templateMatches[0].matchingCriteria,
        error: "No components found in matching templates",
        warnings: [],
      };
    }

    const pricingWarnings: PricingWarning[] = [];

    // Calculate transport cost from tariff if available
    let transportCostFromTariff = 0;
    const isFCL = context.bookingTypeId === 2;
    const isSeafreight = context.transportTypeId === 2;
    const isAirFreight = context.transportTypeId === 1;

    if (selectedTariff) {
      if (isAirFreight) {
        const volumeWeight = bookingData.totalVolume * 167;
        const chargeableWeight = Math.max(bookingData.totalWeight, volumeWeight);
        transportCostFromTariff = chargeableWeight * selectedTariff.price;
        // Diesel surcharge is only applied to LMD costs, not transport costs
      } else if (isSeafreight) {
        if (isFCL) {
          // FCL - per container (use booking type, not raw tariff join shape)
          transportCostFromTariff = selectedTariff.price;
        } else {
          // LCL - per CBM
          transportCostFromTariff =
            bookingData.totalVolume * selectedTariff.price;
        }
      }
    }

    // Shipment type: pallet_threshold from LMD settings (cartons >= this → pallet mode)
    const resolvedPalletThreshold =
      palletThresholdOpt ?? (await getPalletThreshold());
    const isPalletShipment =
      bookingData.totalPallets > 0 ||
      bookingData.totalCartons >= resolvedPalletThreshold;
    const shipmentType = isPalletShipment ? "pallet" : "carton";

    // Process each component
    const calculatedComponents: CalculatedComponent[] = [];
    const currency = templateMatches[0].template.currency || "EUR";

    for (const tc of mergedComponents) {
      if (!tc.pricing_components) continue;

      const component = tc.pricing_components;
      const componentId = tc.component_id;

      const isFclSeafreight = isFCL && isSeafreight;

      // Carton/pallet mode is for LCL/air LMD — not FCL container surcharges
      if (!isFclSeafreight) {
        const appliesToShipmentType = component.applies_to_shipment_type || "both";
        if (appliesToShipmentType !== "both" && appliesToShipmentType !== shipmentType) {
          continue;
        }
      }

      // Skip Inklaringskosten when Fiscale vertegenwoordiging - België is selected
      if (
        componentId === INKLARINGSKOSTEN_COMPONENT_ID &&
        selectedAdditionalCostIds.includes(FISCALE_VERTEGENWOORDIGING_ADDITIONAL_COST_ID)
      ) {
        continue;
      }

      // Apply company override if available
      let finalValue = tc.value !== null ? tc.value : 0;
      let finalIncludedQuantity = tc.included_quantity;
      let finalExtraPrice = tc.extra_price;
      let isDisabled = false;

      if (companyId && companyOverrides.length > 0) {
        const override = companyOverrides.find(
          (o) => o.pricing_component_id === componentId
        );
        if (override) {
          switch (override.override_type) {
            case "fixed_price":
              finalValue = override.fixed_value ?? finalValue;
              finalIncludedQuantity =
                override.fixed_included_products ?? finalIncludedQuantity;
              finalExtraPrice =
                override.fixed_extra_product_cost ?? finalExtraPrice;
              break;
            case "percentage_adjustment": {
              const adjustment = (override.percentage_adjustment || 100) / 100;
              finalValue = finalValue * adjustment;
              finalExtraPrice = finalExtraPrice
                ? finalExtraPrice * adjustment
                : finalExtraPrice;
              break;
            }
            case "disabled":
              isDisabled = true;
              break;
          }
        }
      }

      if (isDisabled) continue;

      // Calculate component value
      const { quantity, totalPrice, calculationDetails } =
        calculateComponentValue(
          { ...tc, value: finalValue, included_quantity: finalIncludedQuantity, extra_price: finalExtraPrice },
          bookingData,
          finalValue,
          {
            isFCL,
            isSeafreight,
            formatEuro,
            t,
          }
        );

      // Filter components based on delivery type configuration
      // Check if component has delivery_type restriction
      const appliesToDeliveryType = component.applies_to_delivery_type;
      if (appliesToDeliveryType) {
        if (isStorageDelivery && appliesToDeliveryType === "delivery") {
          // Skip delivery-only components when storage is selected
          continue;
        }
        if (!isStorageDelivery && appliesToDeliveryType === "storage") {
          // Skip storage-only components when delivery is selected
          continue;
        }
        // If appliesToDeliveryType === "both", include in all cases
      }

      // Filter components based on booking type
      // FCL = Seafreight FCL only. LCL = Seafreight LCL + Airfreight. Both = all
      const appliesToBookingType =
        component.applies_to_booking_type ?? "both";
      if (appliesToBookingType !== "both") {
        if (isAirFreight) {
          // Airfreight: include only "lcl" (LCL+Airfreight) or "both"
          if (appliesToBookingType === "fcl") continue;
        } else if (isSeafreight) {
          if (appliesToBookingType === "fcl" && !isFCL) continue;
          if (appliesToBookingType === "lcl" && isFCL) continue;
        }
      }

      // Filter components based on unloading location (derived from delivery type)
      // FCL + Delivery = at_location (direct to customer, geen lossingskosten)
      // FCL + Storage = at_efreight (goods at E-Freight, wel lossingskosten)
      // LCL/Airfreight = at_efreight (always)
      const appliesToUnloading =
        component.applies_to_unloading_location ?? "both";
      if (appliesToUnloading !== "both") {
        const effectiveUnloading =
          isStorageDelivery
            ? "at_efreight"
            : isFCL && isSeafreight
              ? "at_location"
              : "at_efreight";
        if (appliesToUnloading !== effectiveUnloading) {
          continue;
        }
      }

      const calculationType = component.calculation_type;
      let effectiveCalculationType = calculationType || "fixed";
      if (/administration fee outgoing pallet/i.test(component.name || "")) {
        effectiveCalculationType = "per_pallet";
      } else if (/administration fee outgoing carton/i.test(component.name || "")) {
        effectiveCalculationType = "per_carton";
      }

      if (!isFclSeafreight) {
        if (calculationType === "per_pallet" && bookingData.totalPallets === 0) {
          continue;
        }
        if (
          calculationType === "per_shipment" &&
          bookingData.totalPallets === 0 &&
          component.name?.toLowerCase().includes("pallet")
        ) {
          continue;
        }
      }

      // Filter out zero components if requested (after all filtering)
      if (filterZeroComponents && totalPrice === 0) {
        continue;
      }

      calculatedComponents.push({
        componentId,
        name: component.name || "Unknown",
        description: component.description || null,
        componentType: component.component_type || "other",
        calculationType: effectiveCalculationType,
        value: finalValue,
        currency: tc.currency || "EUR",
        quantity,
        totalPrice,
        // Tiered types bill as one line at totalPrice (base + extras), not base × count
        unitPrice:
          calculationType === "per_unique_product" || calculationType === "per_product"
            ? totalPrice
            : finalValue,
        calculationDetails,
        // Store merged component data for later use (e.g., Extra HS codes logic)
        _mergedComponent: tc, // Internal field for reference
      });
    }

    // Add "Extra HS codes" logic for Inklaringskosten (Customs Clearance) component
    // If there's no per_unique_product customs component, add extra HS code fees
    // based on the Inklaringskosten component's base fee and included quantity
    const hasCustomsPerUnique = calculatedComponents.some(
      (comp) =>
        (comp.componentType?.toLowerCase().includes("customs") ||
          comp.name.toLowerCase().includes("inklaringskosten") ||
          comp.name.toLowerCase().includes("customs")) &&
        comp.calculationType === "per_unique_product"
    );

    if (!hasCustomsPerUnique && bookingData.uniqueHsCodesCount > 0) {
      // Find Inklaringskosten or customs clearance component with per_shipment type
      const inklaringskostenComponent = calculatedComponents.find(
        (comp) =>
          (comp.name.toLowerCase().includes("inklaringskosten") ||
            (comp.componentType?.toLowerCase().includes("customs") &&
              comp.name.toLowerCase().includes("clearance"))) &&
          comp.calculationType === "per_shipment"
      );

      if (inklaringskostenComponent && inklaringskostenComponent._mergedComponent) {
        // Get included quantity from the merged component
        const mergedTc = inklaringskostenComponent._mergedComponent;
        const includedHsCodes = mergedTc.included_quantity || 2; // Default to 2
        const extraHsCodeFee = mergedTc.extra_price || 10; // Default to 10 EUR

        const extraHsCodes = Math.max(
          0,
          bookingData.uniqueHsCodesCount - includedHsCodes
        );

        if (extraHsCodes > 0) {
          const extraCost = extraHsCodes * extraHsCodeFee;
          // Add extra cost to the base component
          inklaringskostenComponent.totalPrice += extraCost;
          inklaringskostenComponent.value += extraCost;
          inklaringskostenComponent.unitPrice = inklaringskostenComponent.totalPrice;
          inklaringskostenComponent.quantity = 1;

          // Update calculation details
          if (formatEuro && t) {
            const basePrice = inklaringskostenComponent.totalPrice - extraCost;
            inklaringskostenComponent.calculationDetails = `${formatEuro(
              basePrice
            )} (${includedHsCodes} ${t(
              "tools.calculator.included",
              "included"
            )}) + ${extraHsCodes} × ${formatEuro(extraHsCodeFee)} = ${formatEuro(
              inklaringskostenComponent.totalPrice
            )}`;
          }
        }
      }
    }

    // Remove internal field before returning (after Extra HS codes logic)
    calculatedComponents.forEach((comp) => {
      if (comp._mergedComponent) {
        delete comp._mergedComponent;
      }
    });

    // Check if transport component exists in template (incl. Dutch: vervoer, vracht)
    const isTransportComponent = (comp: { name?: string }) => {
      const n = (comp.name || "").toLowerCase();
      return (
        n.includes("transport") ||
        n.includes("freight") ||
        n.includes("vervoer") ||
        n.includes("vracht")
      );
    };
    const hasTransportComponent = calculatedComponents.some(isTransportComponent);

    // For FCL seafreight: always use tariff-based transport (container-size-specific).
    // If template has a transport component, override it with the correct tariff price and label.
    if (
      hasTransportComponent &&
      selectedTariff &&
      isFCL &&
      isSeafreight &&
      transportCostFromTariff > 0
    ) {
      const transportComp = calculatedComponents.find(isTransportComponent);
      if (transportComp) {
        transportComp.value = selectedTariff.price;
        transportComp.totalPrice = transportCostFromTariff;
        transportComp.unitPrice = selectedTariff.price;
        const containerLabel = (containerSizeNameOpt ?? selectedTariff.container_size?.name)
          ? `${(containerSizeNameOpt ?? selectedTariff.container_size?.name)} `
          : "";
        const containerWord = t?.("tools.calculator.container", "container") ?? "container";
        const priceStr = formatEuro?.(selectedTariff.price) ?? `€${selectedTariff.price.toFixed(2)}`;
        transportComp.calculationDetails = `1 ${containerLabel}${containerWord} × ${priceStr}`;
      }
    }

    // Add transport cost from tariff if template doesn't include it
    if (!hasTransportComponent && transportCostFromTariff > 0) {
      const baseRateValue =
        isFCL && isSeafreight
          ? 1 // Flat rate for FCL Seafreight
          : isAirFreight
          ? Math.max(
              bookingData.totalWeight,
              bookingData.totalVolume * 167
            ) // Chargeable weight for Airfreight
          : bookingData.totalVolume; // Volume for LCL

      let transportDetails = "";
      if (isAirFreight && selectedTariff) {
        const volumeWeight = bookingData.totalVolume * 167;
        const chargeableWeight = Math.max(
          bookingData.totalWeight,
          volumeWeight
        );
        if (formatEuro && t) {
          if (bookingData.totalWeight > 0 && bookingData.totalVolume > 0) {
            transportDetails = `${t("tools.calculator.actualWeight", "Actual Weight")}: ${bookingData.totalWeight.toFixed(2)} kg | ${t("tools.calculator.volumetricWeight", "Volumetric Weight")}: ${volumeWeight.toFixed(2)} kg (${bookingData.totalVolume} CBM × 167) | ${t("tools.calculator.chargeableWeight", "Chargeable Weight")}: ${chargeableWeight.toFixed(2)} kg (MAX) | ${chargeableWeight.toFixed(2)} kg × ${formatEuro(selectedTariff.price)}`;
          } else {
            transportDetails = `${chargeableWeight.toFixed(2)} kg × ${formatEuro(selectedTariff.price)}`;
          }
        }
      } else if (isSeafreight && selectedTariff) {
        if (isFCL) {
          if (formatEuro && t) {
            const containerLabel = (containerSizeNameOpt ?? selectedTariff.container_size?.name)
              ? `${(containerSizeNameOpt ?? selectedTariff.container_size?.name)} `
              : "";
            transportDetails = `1 ${containerLabel}${t("tools.calculator.container", "container")} × ${formatEuro(selectedTariff.price)}`;
          }
        } else {
          if (formatEuro) {
            transportDetails = `${bookingData.totalVolume} CBM × ${formatEuro(selectedTariff.price)}`;
          }
        }
      }

      calculatedComponents.unshift({
        componentId: 0, // Special ID for tariff-based transport
        name: "Transport Cost",
        description: null,
        componentType: "transport",
        calculationType:
          isFCL && isSeafreight
            ? "per_container"
            : isAirFreight
            ? "per_kg"
            : "per_cbm",
        value: selectedTariff?.price || 0,
        currency: "EUR",
        quantity: baseRateValue,
        totalPrice: transportCostFromTariff,
        unitPrice: selectedTariff?.price || 0,
        calculationDetails: transportDetails,
      });

      // Diesel surcharge is only applied to LMD costs, not transport costs
    }

    // ---- LMD (Last Mile Delivery) ----
    // Include for: delivery + valid LMD country + at least one carton (e.g. LCL / airfreight delivery).
    // Exclude for Seafreight FCL + Delivery: container goes direct to customer, no separate LMD.
    // Note: trim country — whitespace-only values must not skip LMD silently after failed rate lookup.
    const isSeafreightFclDelivery =
      isSeafreight && isFCL && deliveryType === "delivery";
    const lmdCountryTrimmed =
      typeof lmdCountryCode === "string" ? lmdCountryCode.trim() : "";
    const cartonsForLmd = Math.max(0, Number(bookingData.totalCartons) || 0);
    if (
      deliveryType === "delivery" &&
      lmdCountryTrimmed.length > 0 &&
      cartonsForLmd > 0 &&
      !isSeafreightFclDelivery
    ) {
      try {
        const dieselPercent = await getDieselSurchargePercentage();
        // Reuse shipment type from template filtering (respects force-pallet + totalPallets)
        const forceCartonRate = !isPalletShipment;
        // Pallet shipments must always resolve to ≥1 pallet for rate lookup (never 0).
        // Pass real carton count for description / diesel rules; forceCartonRate selects rate mode.
        const rawPallets = isPalletShipment
          ? bookingData.totalPallets > 0
            ? Number(bookingData.totalPallets)
            : Math.ceil(Number(bookingData.totalVolume) / CBM_PER_PALLET)
          : 0;
        const palletCount = isPalletShipment
          ? Math.max(1, Math.floor(rawPallets) || 1)
          : 0;
        const lmd = await calculateLmdCost(
          lmdCountryTrimmed,
          palletCount,
          cartonsForLmd,
          true,
          dieselPercent,
          forceCartonRate
        );
        calculatedComponents.push({
          componentId: -1,
          name: `LMD ${lmdCountryTrimmed}`,
          description: null,
          componentType: "lmd",
          calculationType: "fixed",
          value: lmd.baseRate,
          currency: "EUR",
          quantity: 1,
          totalPrice: lmd.baseRate,
          unitPrice: lmd.baseRate,
          calculationDetails: lmd.description
            .replace(/ \+ \d+% diesel$/, "")
            .replace(/ \+ €[\d.]+\/pallet (?:NL toeslag|tol|Tol surcharge) \(\d+\)$/, ""),
        });
        if (lmd.dieselSurcharge > 0) {
          const pct = ((lmd.dieselSurcharge / lmd.baseRate) * 100).toFixed(0);
          calculatedComponents.push({
            componentId: -2,
            name: "LMD Diesel Surcharge",
            description: null,
            componentType: "lmd_surcharge",
            calculationType: "percentage",
            value: lmd.dieselSurcharge,
            currency: "EUR",
            quantity: 1,
            totalPrice: lmd.dieselSurcharge,
            unitPrice: lmd.baseRate,
            calculationDetails: formatEuro
              ? `${formatEuro(lmd.baseRate)} × ${pct}%`
              : `€${lmd.baseRate.toFixed(2)} × ${pct}%`,
          });
        }
        if (lmd.palletSurcharge > 0) {
          const surchargePallets = isPalletShipment ? palletCount : lmd.palletCount;
          calculatedComponents.push({
            componentId: -3,
            name: "Tol surcharge",
            description: null,
            componentType: "lmd_nl_pallet_surcharge",
            calculationType: "per_pallet",
            value: lmd.palletSurchargePerPallet,
            currency: "EUR",
            quantity: surchargePallets,
            totalPrice: lmd.palletSurcharge,
            unitPrice: lmd.palletSurchargePerPallet,
            calculationDetails: formatEuro
              ? `${formatEuro(lmd.palletSurchargePerPallet)} × ${surchargePallets} ${t?.("tools.calculator.pallet", "pallet") ?? "pallet"}${surchargePallets !== 1 ? "s" : ""}`
              : `€${lmd.palletSurchargePerPallet.toFixed(2)} × ${surchargePallets} pallet${surchargePallets !== 1 ? "s" : ""}`,
          });
        }
      } catch (e) {
        console.warn("Unified pricing: LMD calculation failed", {
          country: lmdCountryTrimmed,
          cartons: cartonsForLmd,
          deliveryType,
          isSeafreightFclDelivery,
          error: e instanceof Error ? e.message : String(e),
        });
        if (e instanceof LmdRateNotFoundError) {
          pricingWarnings.push({
            key: "tools.calculator.lmdRateNotFound",
            params: {
              country: e.country,
              pallets: e.pallets,
              cartons: e.cartons,
            },
          });
        } else {
          pricingWarnings.push({
            key: "tools.calculator.lmdPricingFailed",
            params: { country: lmdCountryTrimmed },
          });
        }
      }
    }

    // ---- Additional costs (aanvullende kosten) ----
    if (selectedAdditionalCostIds.length > 0) {
      const { data: acRows } = await supabase
        .from("additional_costs")
        .select("id, name, description, cost_eur, calculation_type, default_value, applies_to_countries, applies_to_incoterms")
        .eq("is_active", true)
        .in("id", selectedAdditionalCostIds);
      const applicable = filterAdditionalCosts((acRows || []) as AdditionalCost[], {
        deliveryCountryCode: lmdCountryCode ?? undefined,
        destinationCountryCode: destinationCountryCode ?? undefined,
        incotermId: context.incotermId,
      });
      for (const cost of applicable) {
        let amount =
          additionalCostOverrides[cost.id] ??
          parseNumericValue(cost.cost_eur) ??
          parseNumericValue(cost.default_value) ??
          0;
        if (amount <= 0) continue;

        // Fiscale vertegenwoordiging: add €6.50 per extra HS code (beyond the 1st)
        let calculationDetails: string;
        if (cost.id === FISCALE_VERTEGENWOORDIGING_ADDITIONAL_COST_ID && bookingData.uniqueHsCodesCount > 1) {
          const extraHsCodes = bookingData.uniqueHsCodesCount - 1;
          const extraAmount = extraHsCodes * FISCALE_VERTEGENWOORDIGING_EXTRA_PER_HS_EUR;
          amount += extraAmount;
          // Don't include final "= €X" — the UI appends it to avoid duplication
          calculationDetails =
            formatEuro
              ? `${formatEuro(amount - extraAmount)} + ${extraHsCodes} × ${formatEuro(FISCALE_VERTEGENWOORDIGING_EXTRA_PER_HS_EUR)}`
              : cost.description ?? `${cost.name} - additional cost`;
        } else {
          const isFixedCost = (cost.calculation_type || "fixed") === "fixed";
          calculationDetails =
            isFixedCost && formatEuro
              ? `1 × ${formatEuro(amount)}`
              : cost.description ?? `${cost.name} - additional cost`;
        }

        calculatedComponents.push({
          componentId: cost.id,
          name: cost.name,
          description: cost.description ?? null,
          componentType: "additional_cost",
          calculationType: cost.calculation_type || "fixed",
          value: amount,
          currency: "EUR",
          quantity: 1,
          totalPrice: amount,
          unitPrice: amount,
          calculationDetails,
        });
      }
    }

    // ---- Export clearance (when not FOB) ----
    const isFOB = context.incotermId === INCOTERM_FOB_ID;
    if (includeExportClearance && !isFOB) {
      const amount = exportClearanceAmount ?? 45;
      if (amount > 0) {
        calculatedComponents.push({
          componentId: -3,
          name: "Export Clearance License",
          description: null,
          componentType: "export_clearance",
          calculationType: "fixed",
          value: amount,
          currency: "EUR",
          quantity: 1,
          totalPrice: amount,
          unitPrice: amount,
          calculationDetails: formatEuro ? `1 × ${formatEuro(amount)}` : "Export clearance license fee",
        });
      }
    }

    // ---- Pickup fee ----
    if (pickupFee > 0) {
      calculatedComponents.push({
        componentId: -4,
        name: "Pickup Fee",
        description: null,
        componentType: "pickup_fee",
        calculationType: "fixed",
        value: pickupFee,
        currency: "EUR",
        quantity: 1,
        totalPrice: pickupFee,
        unitPrice: pickupFee,
        calculationDetails: formatEuro ? `1 × ${formatEuro(pickupFee)}` : "Pickup fee",
      });
    }

    // ---- ISCM commodity inspection ----
    if (includeCommodityInspection) {
      const amount = commodityInspectionAmount ?? 75;
      if (amount > 0) {
        calculatedComponents.push({
          componentId: -5,
          name: "Commodity Inspection",
          description: null,
          componentType: "commodity_inspection",
          calculationType: "fixed",
          value: amount,
          currency: "EUR",
          quantity: 1,
          totalPrice: amount,
          unitPrice: amount,
          calculationDetails: formatEuro
            ? `1 × ${formatEuro(amount)}`
            : "ISCM commodity inspection fee",
        });
      }
    }

    // ---- Post Export License ----
    if (postExportLicenseFee > 0) {
      calculatedComponents.push({
        componentId: -6,
        name: "Post Export License",
        description: null,
        componentType: "post_export_license",
        calculationType: "fixed",
        value: postExportLicenseFee,
        currency: "EUR",
        quantity: 1,
        totalPrice: postExportLicenseFee,
        unitPrice: postExportLicenseFee,
        calculationDetails: formatEuro
          ? `1 × ${formatEuro(postExportLicenseFee)}`
          : "Post export license fee",
      });
    }

    // ---- Europallets not exchanged (€15/pallet) ----
    const europalletCount = bookingData.totalPallets || 0;
    const europalletFee = calculateEuropalletNonExchangeFee(
      europalletCount,
      europalletsExchanged,
    );
    if (europalletFee > 0) {
      calculatedComponents.push({
        componentId: -7,
        name: europalletNonExchangeDescription(europalletCount),
        description: null,
        componentType: EUROPALLET.COST_TYPE,
        calculationType: "per_pallet",
        value: EUROPALLET.FEE_PER_PALLET,
        currency: "EUR",
        quantity: europalletCount,
        totalPrice: europalletFee,
        unitPrice: EUROPALLET.FEE_PER_PALLET,
        calculationDetails: formatEuro
          ? `${europalletCount} × ${formatEuro(EUROPALLET.FEE_PER_PALLET)}`
          : `${europalletCount} pallets × €${EUROPALLET.FEE_PER_PALLET}`,
      });
    }

    // Calculate total cost
    const totalCost = calculatedComponents.reduce(
      (sum, comp) => sum + comp.totalPrice,
      0
    );

    return {
      components: calculatedComponents,
      totalCost,
      currency,
      matchedTemplates: templateMatches.map((m) => m.template),
      matchingCriteria: templateMatches[0].matchingCriteria,
      error: null,
      warnings: pricingWarnings,
    };
  } catch (error) {
    console.error("Error calculating pricing from templates:", error);
    return {
      components: [],
      totalCost: 0,
      currency: "EUR",
      matchedTemplates: [],
      matchingCriteria: [],
      error: error instanceof Error ? error.message : "Unknown error",
      warnings: [],
    };
  }
}

